/**
 * ContractDetail page — shows contract info, vendor, terms, amendments timeline,
 * site associations, linked circuits, linked invoices, and lifecycle controls.
 *
 * Requirements: 3.1, 3.4, 3.5, 3.6, 3.7, 27.7
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractService } from '@/services/contractService';
import type { LinkedInvoiceSummary } from '@/services/contractService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AmendmentTimeline } from '@/components/AmendmentTimeline';
import { AIReviewPanel } from '@/components/AIReviewPanel';
import { ComparisonView } from '@/components/ComparisonView';
import { DocumentVersionHistory } from '@/components/DocumentVersionHistory';
import { useAuth } from '@/contexts/AuthContext';

const LIFECYCLE_STATUS_VARIANTS: Record<string, BadgeVariant> = {
  Draft: 'gray',
  'Under Review': 'blue',
  Active: 'green',
  Expiring: 'yellow',
  Expired: 'red',
  Renewed: 'blue',
  Terminated: 'red',
  Archived: 'gray',
};

/** Valid manual transitions from each status. */
const VALID_TRANSITIONS: Record<string, string[]> = {
  Draft: ['Under Review', 'Archived'],
  'Under Review': ['Active', 'Archived'],
  Active: ['Terminated', 'Archived'],
  Expiring: ['Archived'],
  Expired: ['Renewed', 'Archived'],
  Renewed: ['Archived'],
  Terminated: ['Archived'],
  Archived: [],
};

export function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const contractId = Number(id);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTransitionDialog, setShowTransitionDialog] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState<string>('');
  const [renewalContractId, setRenewalContractId] = useState<string>('');

  const canWrite = user?.isAdmin ||
    user?.roles.some((r) =>
      ['Admin', 'Contract Specialist', 'Facilities Admin'].includes(r),
    );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => contractService.getById(contractId),
    enabled: !isNaN(contractId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => contractService.delete(contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      navigate('/contracts');
    },
  });

  const transitionMutation = useMutation({
    mutationFn: (input: { targetStatus: string; renewalLinkedContractId?: number }) =>
      contractService.transitionLifecycle(contractId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setShowTransitionDialog(false);
    },
  });

  const matchMutation = useMutation({
    mutationFn: () => contractService.matchInvoices(contractId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract', contractId] });
    },
  });

  if (isNaN(contractId)) {
    return <ErrorMessage message="Invalid contract ID." />;
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading contract details…" />;
  }

  if (error || !data) {
    return (
      <ErrorMessage
        message={(error as Error)?.message ?? 'Failed to load contract.'}
        onRetry={() => refetch()}
      />
    );
  }

  const { contract, amendments, annualCosts, budgetCategories, siteAssociations, linkedInvoices, spendVariance } = data;
  const validTransitions = VALID_TRANSITIONS[contract.lifecycleStatus] ?? [];

  function handleTransition() {
    if (!selectedTransition) return;
    const input: { targetStatus: string; renewalLinkedContractId?: number } = {
      targetStatus: selectedTransition,
    };
    if (selectedTransition === 'Renewed' && renewalContractId) {
      input.renewalLinkedContractId = Number(renewalContractId);
    }
    transitionMutation.mutate(input);
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <Link
            to="/contracts"
            className="inline-flex items-center gap-1 text-slate-500 text-sm no-underline hover:text-baker-red transition-colors mb-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Contracts
          </Link>
          <h1 className="text-lg font-semibold text-text-primary m-0">{contract.contractTitle}</h1>
          <div className="flex gap-2 mt-2 items-center flex-wrap">
            <StatusBadge
              label={contract.lifecycleStatus}
              variant={LIFECYCLE_STATUS_VARIANTS[contract.lifecycleStatus] ?? 'gray'}
            />
            <StatusBadge label={contract.departmentScope} variant="blue" />
            {contract.contractType && (
              <StatusBadge label={contract.contractType} variant="gray" />
            )}
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/contracts/new?edit=${contractId}`)}
              className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
            >
              Edit
            </button>
            {(contract.lifecycleStatus === 'Expiring' || contract.lifecycleStatus === 'Expired') && (
              <button
                onClick={() => navigate(`/contracts/${contractId}/renewal`)}
                className="px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-sm font-medium text-amber-700 cursor-pointer hover:bg-amber-100 transition-all"
              >
                Renewal Decision
              </button>
            )}
            {validTransitions.length > 0 && (
              <button
                onClick={() => setShowTransitionDialog(true)}
                className="px-4 py-2 rounded-lg border border-baker-red/30 bg-red-50 text-sm font-medium text-baker-red cursor-pointer hover:bg-red-100 transition-all"
              >
                Change Status
              </button>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-600 cursor-pointer hover:bg-red-50 transition-all"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Contract Info */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">Contract Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Vendor</span>
            <p className="mt-0.5 mb-0">
              <Link to={`/vendors/${contract.vendorId}`} className="text-baker-red no-underline font-medium hover:underline">
                Vendor #{contract.vendorId}
              </Link>
            </p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Effective Date</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.effectiveDate ? new Date(contract.effectiveDate).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Expiration Date</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.expirationDate ? new Date(contract.expirationDate).toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Contract Value</span>
            <p className="mt-0.5 mb-0 text-slate-700 font-medium tabular-nums">{contract.contractValue != null ? `${Number(contract.contractValue).toLocaleString()}` : '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Payment Terms</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.paymentTerms ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">GL Code</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.glCode ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Budget Category</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.budgetCategory ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Auto-Renewal</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.autoRenewalTerms ?? '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Cancellation Notice</span>
            <p className="mt-0.5 mb-0 text-slate-700">{contract.cancellationNoticePeriodDays != null ? `${contract.cancellationNoticePeriodDays} days` : '—'}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Created</span>
            <p className="mt-0.5 mb-0 text-slate-700">{new Date(contract.createdAt).toLocaleDateString()}</p>
          </div>
          <div>
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Updated</span>
            <p className="mt-0.5 mb-0 text-slate-700">{new Date(contract.updatedAt).toLocaleDateString()}</p>
          </div>
          {contract.renewalLinkedContractId && (
            <div>
              <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Renewal</span>
              <p className="mt-0.5 mb-0">
                <Link to={`/contracts/${contract.renewalLinkedContractId}`} className="text-baker-red no-underline font-medium hover:underline">
                  Contract #{contract.renewalLinkedContractId}
                </Link>
              </p>
            </div>
          )}
        </div>
        {contract.notes && (
          <div className="mt-4 pt-4 border-t border-border">
            <span className="text-slate-500 text-xs font-medium uppercase tracking-wide">Notes</span>
            <p className="mt-1 mb-0 text-sm text-slate-700 leading-relaxed">{contract.notes}</p>
          </div>
        )}
      </section>

      {/* Budget Categories */}
      {budgetCategories.length > 0 && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">
            Budget Categories ({budgetCategories.length})
          </h2>
          <div className="flex gap-2 flex-wrap">
            {budgetCategories.map((bc) => (
              <StatusBadge key={bc.budgetCategory} label={bc.budgetCategory} variant="blue" />
            ))}
          </div>
        </section>
      )}

      {/* Annual Costs */}
      {annualCosts.length > 0 && (
        <section className="mb-6 bg-white rounded-lg border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-base font-semibold text-text-primary m-0">
              Annual Costs ({annualCosts.length} year{annualCosts.length !== 1 ? 's' : ''})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-transparent">
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Fiscal Year</th>
                  <th className="text-right py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                  <th className="text-left py-3 px-5 text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</th>
                </tr>
              </thead>
              <tbody>
                {annualCosts.map((cost) => (
                  <tr key={cost.contractAnnualCostId} className="border-b border-border-subtle hover:bg-page/80 transition-colors">
                    <td className="py-3 px-5 text-slate-700">{cost.fiscalYear}</td>
                    <td className="py-3 px-5 text-right text-slate-700 font-medium tabular-nums">
                      ${Number(cost.annualAmount).toLocaleString()}
                    </td>
                    <td className="py-3 px-5 text-slate-500">{cost.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Site Associations */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">
          Site Associations ({siteAssociations.length})
        </h2>
        {siteAssociations.length === 0 ? (
          <p className="text-slate-400 text-sm">No sites associated with this contract.</p>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {siteAssociations.map((sa) => (
              <Link
                key={sa.siteNumber}
                to={`/sites/${sa.siteNumber}`}
                className="inline-flex items-center px-3 py-1.5 rounded-lg border border-border bg-page text-baker-red no-underline text-sm font-medium hover:bg-white hover:border-baker-red/30 transition-all"
              >
                Site {sa.siteNumber}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Documents */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">Documents</h2>
        <DocumentVersionHistory
          contractId={contractId}
          egnyteFilePath={contract.egnyteFilePath}
        />
      </section>

      {/* Spend Variance */}
      {spendVariance && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mt-0 mb-4">Spend vs. Contract Value</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
            <div className="bg-page rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-text-primary tabular-nums">
                ${Number(spendVariance.contractValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-500 mt-1">Contract Value</div>
            </div>
            <div className="bg-page rounded-lg p-4 text-center">
              <div className="text-xl font-bold text-text-primary tabular-nums">
                ${Number(spendVariance.totalInvoiced).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-500 mt-1">Total Invoiced</div>
            </div>
            <div className="bg-page rounded-lg p-4 text-center">
              <div className={`text-xl font-bold tabular-nums ${
                spendVariance.status === 'over' ? 'text-red-600' :
                spendVariance.status === 'under' ? 'text-emerald-600' : 'text-slate-700'
              }`}>
                {spendVariance.variance >= 0 ? '+' : ''}${Number(spendVariance.variance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-500 mt-1">Variance</div>
            </div>
            <div className="bg-page rounded-lg p-4 text-center">
              <div className={`text-xl font-bold tabular-nums ${
                spendVariance.status === 'over' ? 'text-red-600' :
                spendVariance.status === 'under' ? 'text-emerald-600' : 'text-slate-700'
              }`}>
                {spendVariance.variancePercent >= 0 ? '+' : ''}{spendVariance.variancePercent.toFixed(1)}%
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {spendVariance.status === 'over' ? 'Over Budget' :
                 spendVariance.status === 'under' ? 'Under Budget' : 'On Track'}
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-3 bg-page rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                spendVariance.status === 'over' ? 'bg-red-500' :
                spendVariance.status === 'under' ? 'bg-emerald-500' : 'bg-baker-accent'
              }`}
              style={{ width: `${Math.min(100, Math.max(0, (spendVariance.totalInvoiced / spendVariance.contractValue) * 100))}%` }}
              role="progressbar"
              aria-valuenow={spendVariance.totalInvoiced}
              aria-valuemin={0}
              aria-valuemax={spendVariance.contractValue}
              aria-label="Spend progress"
            />
          </div>
        </section>
      )}

      {/* Linked Invoices */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-text-primary m-0">
            Linked Invoices ({linkedInvoices?.length ?? 0})
          </h2>
          {canWrite && (
            <button
              onClick={() => matchMutation.mutate()}
              disabled={matchMutation.isPending}
              className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page hover:border-baker-accent/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {matchMutation.isPending ? 'Matching…' : 'Auto-Match Invoices'}
            </button>
          )}
        </div>
        {matchMutation.isSuccess && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
            Matched {matchMutation.data?.totalMatched ?? 0} invoice(s) to this contract.
          </div>
        )}
        {matchMutation.isError && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {(matchMutation.error as Error).message}
          </div>
        )}
        {(!linkedInvoices || linkedInvoices.length === 0) ? (
          <p className="text-slate-400 text-sm">
            No invoices linked to this contract. Use "Auto-Match Invoices" to link invoices from this vendor.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-transparent">
                  <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Coding</th>
                </tr>
              </thead>
              <tbody>
                {(linkedInvoices as LinkedInvoiceSummary[]).map((inv) => (
                  <tr key={inv.invoiceId} className="border-b border-border-subtle hover:bg-page/80 transition-colors">
                    <td className="py-2 px-3">
                      <Link
                        to={`/invoices/${inv.invoiceId}`}
                        className="text-baker-red no-underline font-medium hover:underline"
                      >
                        {inv.invoiceNumber ?? `#${inv.invoiceId}`}
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-slate-700">
                      {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-2 px-3 text-right text-slate-700 font-medium tabular-nums">
                      {inv.totalAmount != null ? `$${Number(inv.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <StatusBadge label={inv.lifecycleStatus} variant="gray" />
                    </td>
                    <td className="py-2 px-3 text-slate-500 text-xs">
                      {inv.codingSummary ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {linkedInvoices.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-border">
                    <td className="py-2 px-3 font-semibold text-slate-700" colSpan={2}>
                      Total ({linkedInvoices.length} invoices)
                    </td>
                    <td className="py-2 px-3 text-right font-bold text-text-primary tabular-nums">
                      ${(linkedInvoices as LinkedInvoiceSummary[]).reduce((sum, inv) => sum + (inv.totalAmount ?? 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </section>

      {/* AI Review */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <AIReviewPanel contractId={contractId} canWrite={!!canWrite} />
      </section>

      {/* Version Comparison — only show when Egnyte file is linked */}
      {contract.egnyteFilePath && (
        <section className="mb-6 bg-white rounded-lg border border-border p-5">
          <ComparisonView
            contractId={contractId}
            canWrite={!!canWrite}
            egnyteFilePath={contract.egnyteFilePath}
          />
        </section>
      )}

      {/* Amendment Timeline */}
      <section className="mb-6 bg-white rounded-lg border border-border p-5">
        <AmendmentTimeline contract={contract} amendments={amendments} />
      </section>

      {/* Lifecycle Transition Dialog */}
      {showTransitionDialog && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]"
          onClick={() => setShowTransitionDialog(false)}
        >
          <div
            className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="m-0 mb-4 text-lg font-semibold text-text-primary">
              Change Contract Status
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Current status: <strong className="text-slate-700">{contract.lifecycleStatus}</strong>
            </p>

            <label className="block mb-4 text-sm font-medium text-slate-700">
              New Status
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition(e.target.value)}
                className="block w-full mt-1.5 py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              >
                <option value="">Select status…</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            {selectedTransition === 'Renewed' && (
              <label className="block mb-4 text-sm font-medium text-slate-700">
                Replacement Contract ID
                <input
                  type="number"
                  value={renewalContractId}
                  onChange={(e) => setRenewalContractId(e.target.value)}
                  placeholder="Enter contract ID"
                  className="block w-full mt-1.5 py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all placeholder:text-slate-400"
                />
              </label>
            )}

            {transitionMutation.error && (
              <p className="text-red-600 text-sm mb-4">
                {(transitionMutation.error as Error).message}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTransitionDialog(false)}
                className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 cursor-pointer hover:bg-page transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleTransition}
                disabled={!selectedTransition || transitionMutation.isPending}
                className="px-4 py-2 rounded-lg border-none bg-baker-red text-white text-sm font-medium cursor-pointer hover:bg-baker-red/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {transitionMutation.isPending ? 'Updating…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Contract"
        message={`Are you sure you want to delete "${contract.contractTitle}"? This will archive the contract record.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
